/**
 * Presentation layer of the auto-animate feature.
 *
 * The preset definitions and the whole scheduling logic live in tweenly-lib
 * (`src/autoAnimate`) — only the editor-specific menu metadata (MUI icon +
 * i18n label key) stays here, because the lib must not depend on MUI/React.
 */

import type { AutoAnimatePresetId } from "tweenly"
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded"
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded"
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded"
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded"
import AspectRatioRoundedIcon from "@mui/icons-material/AspectRatioRounded"
import BlurOnRoundedIcon from "@mui/icons-material/BlurOnRounded"
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded"
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded"
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded"
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded"
import OpenInFullRoundedIcon from "@mui/icons-material/OpenInFullRounded"
import ShuffleRoundedIcon from "@mui/icons-material/ShuffleRounded"
import SwapVertRoundedIcon from "@mui/icons-material/SwapVertRounded"
import ZoomInRoundedIcon from "@mui/icons-material/ZoomInRounded"
import { SvgIconProps } from "@mui/material"

export type AutoAnimatePreset = {
    id: AutoAnimatePresetId
    labelKey: string
    Icon: React.ComponentType<SvgIconProps>
}

export const AUTO_ANIMATE_PRESETS: AutoAnimatePreset[] = [
    { id: "fade", labelKey: "editor:timeline.autoAnimatePresets.fade", Icon: BlurOnRoundedIcon },
    {
        id: "slide-up",
        labelKey: "editor:timeline.autoAnimatePresets.slideUp",
        Icon: ArrowUpwardRoundedIcon,
    },
    {
        id: "slide-down",
        labelKey: "editor:timeline.autoAnimatePresets.slideDown",
        Icon: ArrowDownwardRoundedIcon,
    },
    {
        id: "slide-left",
        labelKey: "editor:timeline.autoAnimatePresets.slideLeft",
        Icon: ArrowBackRoundedIcon,
    },
    {
        id: "slide-right",
        labelKey: "editor:timeline.autoAnimatePresets.slideRight",
        Icon: ArrowForwardRoundedIcon,
    },
    { id: "zoom", labelKey: "editor:timeline.autoAnimatePresets.zoom", Icon: ZoomInRoundedIcon },
    {
        id: "scale-subtle",
        labelKey: "editor:timeline.autoAnimatePresets.scaleSubtle",
        Icon: AspectRatioRoundedIcon,
    },
    {
        id: "bounce",
        labelKey: "editor:timeline.autoAnimatePresets.bounce",
        Icon: SwapVertRoundedIcon,
    },
    {
        id: "expand",
        labelKey: "editor:timeline.autoAnimatePresets.expand",
        Icon: OpenInFullRoundedIcon,
    },
    {
        id: "reveal-left",
        labelKey: "editor:timeline.autoAnimatePresets.revealLeft",
        Icon: ChevronLeftRoundedIcon,
    },
    {
        id: "reveal-right",
        labelKey: "editor:timeline.autoAnimatePresets.revealRight",
        Icon: ChevronRightRoundedIcon,
    },
    {
        id: "reveal-up",
        labelKey: "editor:timeline.autoAnimatePresets.revealUp",
        Icon: ExpandLessRoundedIcon,
    },
    {
        id: "reveal-down",
        labelKey: "editor:timeline.autoAnimatePresets.revealDown",
        Icon: ExpandMoreRoundedIcon,
    },
    {
        id: "random",
        labelKey: "editor:timeline.autoAnimatePresets.random",
        Icon: ShuffleRoundedIcon,
    },
]
